import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';

import { useIwiAffiliationFormContext } from '../../hooks/useIwiAffiliationFormContext';
import { FormStep } from '../FormStep';
import { FormStepActions } from '../FormStepActions';
import { FormSteps } from '../IwiAffiliationFormContext';

import { MaoriLanguage } from '@/components/Accessibility/MaoriLanguage';
import { recordEvents } from '@/components/Analytics/RecordEvents';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';
import { FlowFooter } from '@/components/FlowFooter/FlowFooter';
import { ErrorBanner } from '@/components/InputError/InputError';
import { DefaultLayout } from '@/components/Layouts/Layouts';
import { Radio } from '@/components/Radio/Radio';
import { Body2, Fineprint, H2 } from '@/components/Typography/Typography';
import {
  IMaoriDescentData,
  IsMaoriDescentResponses,
  KnowsIwiResponses,
  KNOWS_IWI_TO_LABEL,
  MAORI_DESCENT_TO_LABEL,
  WHOTHISAFFILIATIONFOR,
} from '@/features/iwiAffiliationForm';
// import { useAnalytics } from '@/tools/analytics';

export const Step1Descent = () => {
  const previousStep = window.history.state.usr?.previousStep;
  const affiliationFor = window.history.state.usr?.affiliationfor;
  const isOnBehalfOf = affiliationFor === WHOTHISAFFILIATIONFOR.ON_BEHALF_OF;
  const navigate = useNavigate();
  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useForm<IMaoriDescentData>();
  const watchIsMaoriDescent = watch('isMaoriDescent');
  const watchDoesKnowIwi = watch('doesKnowIwi');

  const { formData, goToNextStep, setFormData } = useIwiAffiliationFormContext();
  const stepData = formData[FormSteps.MAORI_DESCENT];

  const trackMaoriDescent = (data: IMaoriDescentData) => {
    if (data.isMaoriDescent === IsMaoriDescentResponses.NO) {
      recordEvents({
        action: 'maori-descent-response-submitted',
        label: `Is Māori descent: ${data.isMaoriDescent} | Does know Iwi: Not applicable`,
        data: 'iwi-affiliation-form-progress',
      });
      return;
    }

    recordEvents({
      action: 'maori-descent-response-submitted',
      label: `Is Māori descent: ${data.isMaoriDescent} | Does know Iwi: ${data.doesKnowIwi}`,
      data: 'iwi-affiliation-form-progress',
    });
  };

  const stepSubmitHandler = (data: IMaoriDescentData) => {
    const formDataUpdated = { ...formData, [FormSteps.MAORI_DESCENT]: data };

    trackMaoriDescent(data);
    setFormData(formDataUpdated);
    goToNextStep(formDataUpdated);
  };

  const doesntKnowIwi = (watchDoesKnowIwi || stepData.doesKnowIwi) === KnowsIwiResponses.NO;

  const isMaoriDescent =
    (watchIsMaoriDescent || stepData.isMaoriDescent) === IsMaoriDescentResponses.YES ||
    (watchIsMaoriDescent || stepData.isMaoriDescent) === IsMaoriDescentResponses.DONT_KNOW;

  const isMaoriDescentInputProps = register('isMaoriDescent', { required: true });
  const [showMaoriDescentWarning, setShowMaoriDescentWarning] = useState<boolean>(false);

  const onMaoriDescentChanged = (e: any) => {
    if (stepData.isMaoriDescent === IsMaoriDescentResponses.YES) {
      setShowMaoriDescentWarning(true);
    } else {
      isMaoriDescentInputProps.onChange(e);
    }
  };

  const handleChangeMaoriDescent = (isMaoriDescent: IsMaoriDescentResponses) => {
    setShowMaoriDescentWarning(false);

    setValue('isMaoriDescent', isMaoriDescent);
  };

  return (
    <Wrapper>
      <DefaultLayout>
        <ConfirmationDialog
          isOpen={showMaoriDescentWarning}
          id="maoriDescentWarning"
          dialogLabel="If you switch your answer to No, you will lose the iwi information have entered."
          onConfirm={() => handleChangeMaoriDescent(IsMaoriDescentResponses.NO)}
          onConfirmLabel="Yes, switch answer"
          onCancel={() => handleChangeMaoriDescent(IsMaoriDescentResponses.YES)}
          onCancelLabel="Cancel"
        />
        <FormStep
          onSubmit={handleSubmit(stepSubmitHandler)}
          aria-labelledby="label_formStep"
          goToPreviousStep={() => {
            window.scrollTo(0, 0);
            previousStep === 'manage'
              ? navigate('/manage', { state: { redirectedFrom: 'step1form' } })
              : navigate('/who-is-this-iwi-affiliation-for', { state: { redirectedFrom: 'step1form' } });
          }}
          pt="8px"
          px={{ md: '32px ' }}
          mt={{ _: '65px', md: '144px' }}
        >
          <Box
            mt={{ _: '65px', md: '120px' }}
            flexGrow={1}
            flexShrink={0}
            display="flex"
            flexDirection="column"
            pb={36}
            maxWidth={850}
          >
            <MaoriDescentQuestionWrapper>
              <Box as="fieldset" width="100%">
                <Box display="flex" flexShrink={0} flexGrow={0} flexDirection="column" alignItems={{ md: 'center' }}>
                  <H2 color="white" as="legend">
                    {isOnBehalfOf ? 'Do they have ' : 'Do you have '}
                    <MaoriLanguage>Māori</MaoriLanguage> ancestry or <MaoriLanguage>whakapapa</MaoriLanguage>?
                  </H2>
                  <Body2 mt="16px" color="white">
                    That is, did {isOnBehalfOf ? 'they' : 'you'} have <MaoriLanguage>Māori</MaoriLanguage> birth
                    parents, grandparents or great-grandparents, etc.
                  </Body2>
                  <Box width="100%" maxWidth={{ md: 550 }}>
                    {errors.isMaoriDescent && (
                      <Box mt={12}>
                        <ErrorBanner>
                          Please indicate if {isOnBehalfOf ? 'they are' : 'you are'} of{' '}
                          <MaoriLanguage>Māori</MaoriLanguage> descent.
                        </ErrorBanner>
                      </Box>
                    )}
                    <RadioGroup>
                      <Radio
                        data-cy="is-maori-descent-yes"
                        label={MAORI_DESCENT_TO_LABEL[IsMaoriDescentResponses.YES]}
                        id="is-maori-descent-yes"
                        value={IsMaoriDescentResponses.YES}
                        {...isMaoriDescentInputProps}
                        onBlur={isMaoriDescentInputProps.onBlur}
                        defaultChecked={stepData.isMaoriDescent === IsMaoriDescentResponses.YES}
                      />
                      <Radio
                        label={MAORI_DESCENT_TO_LABEL[IsMaoriDescentResponses.DONT_KNOW]}
                        id="is-maori-descent-dont-know"
                        value={IsMaoriDescentResponses.DONT_KNOW}
                        {...isMaoriDescentInputProps}
                        onBlur={isMaoriDescentInputProps.onBlur}
                        defaultChecked={stepData.isMaoriDescent === IsMaoriDescentResponses.DONT_KNOW}
                      />
                      <Radio
                        data-cy="is-maori-descent-no"
                        label={MAORI_DESCENT_TO_LABEL[IsMaoriDescentResponses.NO]}
                        id="is-maori-descent-no"
                        value={IsMaoriDescentResponses.NO}
                        {...isMaoriDescentInputProps}
                        onBlur={isMaoriDescentInputProps.onBlur}
                        onChange={onMaoriDescentChanged}
                        defaultChecked={stepData.isMaoriDescent === IsMaoriDescentResponses.NO}
                      />
                    </RadioGroup>
                  </Box>
                </Box>
              </Box>
            </MaoriDescentQuestionWrapper>

            {isMaoriDescent && (
              <MaoriDescentQuestionWrapper>
                <Box as="fieldset" width="100%">
                  <Box display="flex" flexShrink={0} flexDirection="column" alignItems={{ md: 'center' }}>
                    <H2 color="white" as="legend">
                      Do you know the name(s) of {isOnBehalfOf ? 'their' : 'your'} <MaoriLanguage>iwi</MaoriLanguage>{' '}
                      (tribe)?
                    </H2>
                    <Body2 color="white" mt={16} textAlign="center">
                      The Ministry of Health | <MaoriLanguage>Manatū Hauora</MaoriLanguage>, in partnership with the{' '}
                      <MaoriLanguage>Tātai Māori</MaoriLanguage>-led Governance Group, will collect the following
                      information to understand who {isOnBehalfOf ? 'they are' : 'you are'} in relation to the
                      <MaoriLanguage> iwi</MaoriLanguage> {isOnBehalfOf ? 'they affiliate or' : 'you affiliate or'}{' '}
                      <MaoriLanguage>whakapapa</MaoriLanguage> to.
                    </Body2>
                    <Box width="100%" maxWidth={{ md: 550 }}>
                      {errors.doesKnowIwi && (
                        <Box mt={12}>
                          <ErrorBanner>
                            Please indicate if {isOnBehalfOf ? 'they know' : 'you know'}{' '}
                            <MaoriLanguage>iwi</MaoriLanguage>.
                          </ErrorBanner>
                        </Box>
                      )}
                      <RadioGroup>
                        <Radio
                          data-cy="radio-does-know-iwi-yes"
                          label={KNOWS_IWI_TO_LABEL[KnowsIwiResponses.YES]}
                          id="radio-does-know-iwi-yes"
                          {...register('doesKnowIwi', { required: true })}
                          value={KnowsIwiResponses.YES}
                          defaultChecked={stepData.doesKnowIwi === KnowsIwiResponses.YES}
                        />
                        <Radio
                          label={KNOWS_IWI_TO_LABEL[KnowsIwiResponses.PARTIALLY]}
                          id="radio-does-know-iwi-know-some"
                          {...register('doesKnowIwi', { required: true })}
                          value={KnowsIwiResponses.PARTIALLY}
                          defaultChecked={stepData.doesKnowIwi === KnowsIwiResponses.PARTIALLY}
                        />
                        <Radio
                          label={KNOWS_IWI_TO_LABEL[KnowsIwiResponses.NO]}
                          id="radio-does-know-iwi-no"
                          {...register('doesKnowIwi', { required: true })}
                          value={KnowsIwiResponses.NO}
                          defaultChecked={stepData.doesKnowIwi === KnowsIwiResponses.NO}
                        >
                          {doesntKnowIwi && (
                            <>
                              <Fineprint mb="1rem" fontWeight={400} textAlign="left">
                                If you are unsure of the name of {isOnBehalfOf ? 'their' : 'your'} iwi, we have provided
                                a map and a list that you can filter by region to view all iwi from that rohe in the
                                next step.
                              </Fineprint>
                              <Fineprint fontWeight={400} textAlign="left">
                                If you are still unsure after reviewing the list, we encourage you to talk with{' '}
                                {isOnBehalfOf ? 'their' : 'your'} <MaoriLanguage>whānau</MaoriLanguage> and return to
                                the website to provide this information at a later time. You can still record that{' '}
                                {isOnBehalfOf ? 'they are' : 'you are'} of <MaoriLanguage>Māori</MaoriLanguage> descent
                                without identifying {isOnBehalfOf ? 'their' : 'your'} <MaoriLanguage>iwi</MaoriLanguage>
                                .
                              </Fineprint>
                            </>
                          )}
                        </Radio>
                      </RadioGroup>
                    </Box>
                  </Box>
                </Box>
              </MaoriDescentQuestionWrapper>
            )}

            <FormStepActions>
              <Button label="Next" type="submit" data-cy="next-button" />
            </FormStepActions>
          </Box>
        </FormStep>
      </DefaultLayout>
      <FlowFooter />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MaoriDescentQuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;

  ${up('md')} {
    text-align: center;
    margin-bottom: 56px;

    :first-child {
      margin-bottom: 80px;
    }
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;

  label:last-of-type {
    margin-bottom: 0;
  }
`;
